import { DateTime } from "luxon";
import { Box } from "../../components/Box";
import { Flex } from "../../components/Flex";
import { Paragraph } from "../../components/Paragraph";
import { Image } from "../../components/Image";
import { CheckStatusEnum } from "../../context/MandateStatusContext";
import accept from "../../assets/accept.svg";
import reject from "../../assets/reject.svg";
import { ApprovalCommentType } from "../../types/Individual";
import { CHECK_DECISION_LABEL_COLOR } from "./screeningResultHelpers";

type Props = {
  data: ApprovalCommentType;
  status: string;
};

/**
 * Who decided a check, and why — shared by every check type's manual
 * accept/reject state, from the Applications sliders to the Screening record
 * panel. One design here, so a decision reads the same everywhere an officer
 * meets one.
 */
export const ApprovalComment = ({ data, status }: Props) => {
  const isAccepted = status === CheckStatusEnum.ManuallyAccepted;
  const isRejected = status === CheckStatusEnum.ManuallyRejected;
  if (!isAccepted && !isRejected) return null;

  const name = [data?.first_name, data?.last_name].filter(Boolean).join(" ");
  const label = String(status).replace(/_/g, " ").toUpperCase();
  const labelColor = CHECK_DECISION_LABEL_COLOR[status] || "gray.60";

  return (
    <Flex gap="12" p={4} alignItems="flex-start">
      <Image
        src={isAccepted ? accept : reject}
        alt={isAccepted ? "Manually accepted" : "Manually rejected"}
        height={32}
        width={32}
      />
      <Box flex={1}>
        <Flex justifyContent="space-between" alignItems="baseline" gap="8">
          <Paragraph m="0px" fontSize={1}>
            <Box as="span" fontWeight="700" color={labelColor}>
              {label}
            </Box>
            {name && (
              <Box as="span" color="gray.80" fontWeight="600">
                {" "}
                · {name}
              </Box>
            )}
          </Paragraph>
          {data?.date && (
            <Paragraph
              m="0px"
              color="gray.50"
              fontSize={0}
              style={{ whiteSpace: "nowrap" }}
            >
              {DateTime.fromISO(data.date).toFormat("dd LLL, yyyy")}
            </Paragraph>
          )}
        </Flex>
        {data?.comment && (
          <Paragraph my={1} color="gray.80">
            &ldquo;{data.comment}&rdquo;
          </Paragraph>
        )}
      </Box>
    </Flex>
  );
};
